import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Container } from '../Layout/ResponsiveGrid';
import { Code, GitBranch, TestTube, Database, Server, Globe } from 'lucide-react';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

// Styled components
const AboutSection = styled.section`
  padding: 6rem 0;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 4rem 0;
  }
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 80px;
    height: 4px;
    background: ${({ theme }) => theme.colors.accent};
    border-radius: 2px;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const AboutContent = styled.div`
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const BioText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const Highlight = styled.span`
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 600;
`;

const SkillsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const SkillCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${slideInRight} 0.4s ease-out forwards;
  animation-delay: ${({ $index }) => `${0.1 * $index}s`};
  opacity: 0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.accent};
    
    .icon-wrapper {
      background: ${({ theme }) => theme.colors.accent};
      color: white;
    }
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => `${theme.colors.accent}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  color: ${({ theme }) => theme.colors.accent};
`;

const SkillName = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.5rem;
`;

const SkillDescription = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ExperienceCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  box-shadow: ${({ theme }) => theme.shadows.md};
  animation: ${fadeIn} 1s ease-out forwards;
  animation-delay: 0.3s;
  opacity: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const ExperienceHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ExperienceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.5rem;
`;

const ExperienceSubtitle = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 500;
`;

const ExperienceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ExperienceItem = styled.li`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ExperienceIcon = styled.div`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors.accent}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.accent};
`;

const ExperienceContent = styled.div``;

const ExperienceItemTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.25rem;
`;

const ExperienceItemText = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 2.5rem;
`;

const Tag = styled.span`
  background: ${({ theme }) => `${theme.colors.accent}15`};
  color: ${({ theme }) => theme.colors.accent};
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    color: white;
    transform: translateY(-2px);
  }
`;

const skills = [
  { name: 'C++', icon: Code, description: 'Primary language' },
  { name: 'Python', icon: Code, description: 'Automation & tools' },
  { name: 'C#', icon: Code, description: 'Application dev' },
  { name: 'Web Dev', icon: Globe, description: 'Frontend skills' },
  { name: 'Database', icon: Database, description: 'SQL expertise' },
  { name: 'TDD', icon: TestTube, description: 'Testing focus' },
];

const RedesignedAbout = () => {
  return (
    <AboutSection id="about">
      <Container>
        <SectionHeading>About Me</SectionHeading>
        
        <AboutGrid>
          <AboutContent>
            <BioText>
              I am a <Highlight>passionate software developer</Highlight> with a strong focus on clean code 
              and test-driven development. My goal is to create robust, maintainable, and high-quality 
              software solutions that follow modern best practices.
            </BioText>
            
            <BioText>
              Beyond C++, I have experience in Python and C#, as well as SQL for database applications. 
              Writing reliable code is a priority for me, which is why I emphasize unit and integration 
              testing using frameworks like <Highlight>Google Test</Highlight> and <Highlight>Google Mock</Highlight>.
            </BioText>
            
            <BioText>
              I enjoy working with agile methodologies like Scrum and Flow-Design, leveraging Git for 
              version control, and exploring new technologies to refine my development skills.
            </BioText>
            
            <TagsContainer>
              {['C++', 'Python', 'C#', 'SQL', 'Test-Driven Development', 'Agile Methodologies', 'Git', 'HTML5', 'CSS3', 'JavaScript', 'React'].map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </TagsContainer>
          </AboutContent>
          
          <ExperienceCard>
            <ExperienceHeader>
              <ExperienceTitle>Core Expertise</ExperienceTitle>
              <ExperienceSubtitle>Key technologies and methodologies</ExperienceSubtitle>
            </ExperienceHeader>
            
            <ExperienceList>
              <ExperienceItem>
                <ExperienceIcon>
                  <Code size={20} />
                </ExperienceIcon>
                <ExperienceContent>
                  <ExperienceItemTitle>Software Development</ExperienceItemTitle>
                  <ExperienceItemText>
                    Proficient in creating robust applications with C++, Python and C#, 
                    consistently applying clean code principles for maintainability.
                  </ExperienceItemText>
                </ExperienceContent>
              </ExperienceItem>
              
              <ExperienceItem>
                <ExperienceIcon>
                  <TestTube size={20} />
                </ExperienceIcon>
                <ExperienceContent>
                  <ExperienceItemTitle>Test-Driven Development</ExperienceItemTitle>
                  <ExperienceItemText>
                    Implementing comprehensive test suites with GoogleTest and PyTest to ensure 
                    code reliability and facilitate refactoring with confidence.
                  </ExperienceItemText>
                </ExperienceContent>
              </ExperienceItem>
              
              <ExperienceItem>
                <ExperienceIcon>
                  <GitBranch size={20} />
                </ExperienceIcon>
                <ExperienceContent>
                  <ExperienceItemTitle>Version Control & CI/CD</ExperienceItemTitle>
                  <ExperienceItemText>
                    Experienced with Git workflows and continuous integration practices 
                    to maintain code quality and streamline development processes.
                  </ExperienceItemText>
                </ExperienceContent>
              </ExperienceItem>
              
              <ExperienceItem>
                <ExperienceIcon>
                  <Server size={20} />
                </ExperienceIcon>
                <ExperienceContent>
                  <ExperienceItemTitle>Database Design</ExperienceItemTitle>
                  <ExperienceItemText>
                    Designing and implementing efficient database solutions with SQL,
                    ensuring data integrity and optimized performance.
                  </ExperienceItemText>
                </ExperienceContent>
              </ExperienceItem>
            </ExperienceList>
          </ExperienceCard>
        </AboutGrid>
        
        <SkillsContainer>
          {skills.map((skill, index) => {
            const Icon = skill.icon;
            return (
              <SkillCard key={index} $index={index}>
                <IconWrapper>
                  <Icon size={24} />
                </IconWrapper>
                <SkillName>{skill.name}</SkillName>
                <SkillDescription>{skill.description}</SkillDescription>
              </SkillCard>
            );
          })}
        </SkillsContainer>
      </Container>
    </AboutSection>
  );
};

export default RedesignedAbout;